import { css, Global } from '@emotion/react'
import { ReactNode, ElementType } from 'react'
import { QueryClientProviderProps } from '@tanstack/react-query'
import { isNextJsPage } from '~/common/nextJsUtils'
import { useRenderMaybeProviders } from '~/common/AllProviders'
import { PageErrorBoundary } from '~/components/v2/ErrorBoundary'
import GlobalCSSVariables from '~/theme/css/workdayTheme'
import { ChakraProviderProps } from '~/design_system'
import { TestIntlProviderProps } from '~/common/localizationTestingUtils'

export interface ChakraWrapperProps {
  children: ReactNode
  globalShims?: boolean
  wrappingDiv?: boolean
  chakraProviderProps?: Partial<ChakraProviderProps>
  reactQueryClientProviderProps?: Partial<QueryClientProviderProps>
  intlProviderProps?: Partial<TestIntlProviderProps>
  TestIntlProvider?: ElementType
}

/**
 * ChakraWrapper
 *
 * Still used only by SharedReactRootManager to optionally render globalShims and a wrappingDiv.
 *
 * Todo: Remove this component and all usages of it.
 *
 * @deprecated
 *
 * In most cases you should not be wrapping your components or pages in additional providers.
 *
 * The following already have Providers and you don't need to add additional ones to the page:
 * - Next.js pages and all components on them
 * - React components rendered onto a Django page via renderIntoSharedReactRoot or asJqueryPlugin/$.react()
 * - Tests using `render` from ~/common/testUtils (most tests should use this)
 *
 * If you must add providers, and know what you are doing, please use <AllProviders> instead.
 */
export const ChakraWrapper = ({
  children,
  globalShims = true,
  wrappingDiv = true,
  chakraProviderProps,
  reactQueryClientProviderProps,
  TestIntlProvider,
  intlProviderProps
}: ChakraWrapperProps) => {
  const renderProviders = useRenderMaybeProviders()

  if (isNextJsPage()) return children

  return (
    <>
      {renderProviders.all({
        chakraProviderProps,
        reactQueryClientProviderProps,
        intlProviderProps,
        TestIntlProvider,
        children: (
          <>
            <GlobalCSSVariables />
            {globalShims && (
              <Global
                styles={css`
                  html {
                    font-size: 16px !important;
                  }

                  .top-nav img,
                  .menu-list img {
                    display: inline;
                  }

                  .isChakra div[role='radiogroup'] label,
                  .isChakra div[role='group'] label,
                  .isChakra label {
                    font-weight: normal; /* less.forms makes this bold */
                  }

                  .isChakra a {
                    font-size: inherit;
                  }

                  .isChakra.drawer {
                    top: 60px !important;
                    height: calc(100% - 60px) !important;
                  }

                  .isChakra h1,
                  .isChakra h2,
                  .isChakra h3,
                  .isChakra h4,
                  .isChakra h5,
                  .isChakra h6 {
                    margin: 0;
                  }
                `}
              />
            )}
            <PageErrorBoundary>{wrappingDiv ? <div className="isChakra">{children}</div> : children}</PageErrorBoundary>
          </>
        )
      })}
    </>
  )
}
